import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { GameStateView } from "@shared/types";
import type { ConnStatus, FatalReason } from "../lib/room";
import { useI18n, useT } from "../lib/i18n";
import { applyTheme } from "../lib/theme";
import { Logo } from "./Brand";
import { Icon, type IconName } from "./Icon";
import { Spinner } from "./ui";

/**
 * Wraps every in-room screen with the three things they all need:
 *   • the host's theme applied to this device
 *   • the room's default language adopted (unless the viewer chose their own)
 *   • an honest connection state instead of a silently frozen board
 */
export function RoomFrame({
  state,
  status,
  fatal,
  children,
}: {
  state: GameStateView | null;
  status: ConnStatus;
  fatal: FatalReason | null;
  children: ReactNode;
}) {
  const { suggestLang } = useI18n();
  const t = useT();

  const theme = state?.settings.theme;
  const roomLang = state?.settings.language;

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (roomLang) suggestLang(roomLang);
  }, [roomLang, suggestLang]);

  if (fatal) {
    const copy: Record<FatalReason, { icon: IconName; message: string }> = {
      badToken: { icon: "key", message: t("badToken") },
      full: { icon: "users", message: t("roomFull") },
      notFound: { icon: "doorOpen", message: t("roomClosed") },
      removed: { icon: "doorOpen", message: t("roomClosed") },
    };
    const { icon, message } = copy[fatal];
    return (
      <div className="min-h-full flex flex-col items-center justify-center gap-5 p-8 text-center">
        <Icon name={icon} size="2.5rem" className="text-ink/30" />
        <p className="text-lg text-ink/70 max-w-sm">{message}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-control bg-accent text-onAccent font-semibold hover:bg-accentLight transition-colors"
        >
          <Icon name="arrowLeft" />
          {t("appName")}
        </Link>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center gap-4 text-ink/50">
        <Logo size={40} to={null} />
        <span className="inline-flex items-center gap-2 text-sm">
          <Spinner />
          {status === "reconnecting" ? t("reconnecting") : t("connecting")}
        </span>
      </div>
    );
  }

  return (
    <>
      {children}
      {/*
        A frozen board with no explanation is the worst failure mode here — the
        host thinks the app broke. This says what is happening without stealing
        the screen.
      */}
      {status !== "open" && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 inline-flex items-center gap-2 h-10 px-4 rounded-pill border border-line text-ink/85 text-sm shadow-pop anim-fade-in"
          style={{ background: "var(--c-panel)" }}
        >
          <Icon name="wifiOff" className="text-warning" />
          {t("connectionLost")}
        </div>
      )}
    </>
  );
}
